const React = require('react');
const PropTypes = require('prop-types');

const {
  MERCADOPAGO_SDK_TOKENIZE_ERRORS,
  MERCADOPAGO_SDK_TOKENIZE_CARD_TOKEN,
} = require('../../spa/actions/types');
const { getValueFromQuery, getQueryParams } = require('../Dom');
const {
  ERROR_SPA: { SDK_TOKENIZATION_ERROR },
} = require('../../../constants/app');
const { SITE_ID } = require('../../../constants/commons');
const { navigateToException } = require('../navigateTo');
const { logErrorFromClient } = require('../logTags');

/* istanbul ignore next */
class ValidatorProvider extends React.Component {
  constructor(props) {
    super(props);
    this.components = [];
    this.secureFieldsRegistered = [];
    this.register = this.register.bind(this);
    this.unregister = this.unregister.bind(this);
    this.isValid = this.isValid.bind(this);
    this.getInvalid = this.getInvalid.bind(this);
    this.isSecureFieldsValid = this.isSecureFieldsValid.bind(this);
    this.registerSecureField = this.registerSecureField.bind(this);
    this.unregisterSecureField = this.unregisterSecureField.bind(this);
  }

  /**
   * Register child components that need to be validated
   * @returns {{register: ValidatorProvider.register}}
   */
  getChildContext() {
    return {
      register: this.register,
      unregister: this.unregister,
      isValid: this.isValid,
      getInvalid: this.getInvalid,
      isSecureFieldsValid: this.isSecureFieldsValid,
      registerSecureField: this.registerSecureField,
      unregisterSecureField: this.unregisterSecureField,
    };
  }

  /**
   * Check if the components are valid using validate method (User needs to implement this on the components)
   * You can choose if you want the validator breaks on any invalid component
   * @param breakOnInvalid
   * @returns {Array}
   */
  getInvalid(breakOnInvalid = false) {
    let i = 0;
    const componentsLength = this.components.length;
    const invalid = [];

    for (; i < componentsLength; i++) {
      if (this.components[i].validate) {
        // If the method returns TRUE it means that is valid
        if (!this.components[i].validate()) {
          invalid.push(this.components[i]);
          if (breakOnInvalid) {
            break;
          }
        }
      }
    }

    return invalid;
  }

  /**
   * Register component that needs validation
   * @param reference
   */
  register(reference) {
    if (reference) {
      // Add the component reference to the list
      this.components.push(reference);
    }
  }

  /**
   * This method is going to remove from components list the reference that is unmount
   * @param reference
   */
  unregister(reference) {
    let indexToRemove = -1;

    this.components.forEach((ref, index) => {
      if (ref === reference) {
        indexToRemove = index;
      }
    });

    // Remove specific element from list
    if (indexToRemove !== -1) {
      this.components.splice(indexToRemove, 1);
    }
  }

  /**
   * Register a component that needs validation
   * @param reference
   */
  registerSecureField(reference) {
    this.secureFieldsRegistered?.push(reference);
  }

  unregisterSecureField(reference) {
    this.secureFieldsRegistered = this.secureFieldsRegistered.filter(
      (field) => field !== reference,
    );
  }

  /**
   * Check if the components are valid
   * You can choose if you want the validator breaks on any invalid component
   * @param breakOnInvalid
   * @returns {boolean}
   */
  isValid(breakOnInvalid = false) {
    const components = this.getInvalid(breakOnInvalid);
    return components.length === 0;
  }

  /**
   * @WARNING: The cardTokenResponse needs to be generated in
   * validation time because the exception is what tells if the fields are valid or not
   */
  async isSecureFieldsValid(mercadoPagoSdkActions) {
    if (this.secureFieldsRegistered.length === 0) {
      return true;
    }

    try {
      const formRef = document
        .querySelector('[name*=fullname]')
        ?.closest('form');
      const cardForm = formRef;

      const { MercadoPagoSDK } = window;
      const cardTokenResponse = await MercadoPagoSDK.fields.createCardToken({
        cardholderName: getValueFromQuery(cardForm, '[name*=fullname]'),
      });

      if (cardTokenResponse.id) {
        // Set the card_token to the input on form
        mercadoPagoSdkActions[MERCADOPAGO_SDK_TOKENIZE_CARD_TOKEN]({
          cardToken: cardTokenResponse.id,
        });
        mercadoPagoSdkActions[MERCADOPAGO_SDK_TOKENIZE_ERRORS]([]);
      }

      if (
        this.props.siteId === SITE_ID.MLA ||
        this.props.siteId === SITE_ID.MLB
      ) {
        // This second cardToken is used to validate ZeroDollar
        const cardTokenResponseZeroDollar =
          await MercadoPagoSDK.fields.createCardToken({
            cardholderName: getValueFromQuery(cardForm, '[name*=fullname]'),
          });

        if (cardTokenResponse.id && cardTokenResponseZeroDollar.id) {
          // Set the card_token to the input on form
          mercadoPagoSdkActions[MERCADOPAGO_SDK_TOKENIZE_CARD_TOKEN]({
            cardToken: cardTokenResponse.id,
            zeroDollarCardToken: cardTokenResponseZeroDollar.id,
          });
          mercadoPagoSdkActions[MERCADOPAGO_SDK_TOKENIZE_ERRORS]([]);
        }
      }

      return true;
    } catch (error) {
      // Get the response data from error object
      if (Array.isArray(error) && error.length >= 0) {
        mercadoPagoSdkActions[MERCADOPAGO_SDK_TOKENIZE_ERRORS](error);

        return false;
      }

      const logError = {
        name: error?.name || error?.reason || 'Error',
        message: error?.message,
      };

      const { data_error } = logErrorFromClient(
        logError,
        SDK_TOKENIZATION_ERROR,
        '[withSDKTokenization][WrappedCardForm][onStepSubmit]',
        { props: this.props },
      );

      // If the token was unable to create or was unable to update, the checkout cant continue
      navigateToException(
        this.props.history,
        this.props.type,
        getQueryParams(),
        data_error,
        SDK_TOKENIZATION_ERROR,
      );

      return false;
    }
  }

  /**
   * Render child element
   */
  render() {
    // Prevents using a <div> to containe the element
    return React.Children.only(this.props.children);
  }
}

/**
 * Define the childComponentTypes
 */
ValidatorProvider.childContextTypes = {
  register: PropTypes.func,
  unregister: PropTypes.func,
  isValid: PropTypes.func,
  getInvalid: PropTypes.func,
  isSecureFieldsValid: PropTypes.func,
  registerSecureField: PropTypes.func,
  unregisterSecureField: PropTypes.func,
};

module.exports = ValidatorProvider;
